import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FloppyDisk, ImageSquare, Trash } from "@phosphor-icons/react";

/**
 * Company Profile (extended) + Invoice Series settings.
 * Split into two exported components so Settings.jsx can drop them in.
 */

export function CompanyProfileCard() {
    const [profile, setProfile] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logoBusy, setLogoBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/company-profile");
            setProfile(data || {});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const set = (k) => (v) => setProfile((p) => ({ ...p, [k]: typeof v === "string" ? v : v.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...profile };
            delete payload.company_name;      // owned by CompanyIdentitySettings
            delete payload.company_gstin;
            await api.put("/company-profile", payload);
            toast.success("Company profile saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const onLogo = async (file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) return toast.error("Logo must be under 4 MB");
        setLogoBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/company-profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const next = { ...profile, logo_document_id: data.document_id };
            const payload = { ...next };
            delete payload.company_name; delete payload.company_gstin;
            await api.put("/company-profile", payload);
            setProfile(next);
            toast.success("Logo uploaded");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLogoBusy(false); }
    };

    const clearLogo = async () => {
        const next = { ...profile, logo_document_id: null };
        const payload = { ...next };
        delete payload.company_name; delete payload.company_gstin;
        try {
            await api.put("/company-profile", payload);
            setProfile(next);
            toast.success("Logo removed");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="company-profile-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Company profile for invoices</div>
                <div className="text-xs text-stone-500">
                    Printed at the top of every sales invoice PDF. Legal name + GSTIN are edited in the Company identity card above.
                </div>
            </div>
            <div className="p-5 space-y-4">
                {/* Logo */}
                <div className="flex items-center gap-4">
                    <div className="w-24 h-24 border rounded-sm flex items-center justify-center bg-stone-50 overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                        {profile.logo_document_id ? (
                            <img src={`${api.defaults.baseURL}/documents/${profile.logo_document_id}/download`}
                                 alt="logo" className="max-w-full max-h-full object-contain"
                                 data-testid="company-logo-preview" />
                        ) : (
                            <ImageSquare size={32} className="text-stone-400" />
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border rounded-sm cursor-pointer hover:bg-stone-50"
                               style={{ borderColor: "var(--se-border)" }}
                               data-testid="company-logo-upload">
                            <input type="file" accept="image/*" className="hidden"
                                   disabled={logoBusy}
                                   onChange={(e) => onLogo(e.target.files?.[0])} />
                            {logoBusy ? "Uploading…" : (profile.logo_document_id ? "Replace logo" : "Upload logo")}
                        </label>
                        {profile.logo_document_id ? (
                            <button onClick={clearLogo} className="text-[11px] text-rose-600 hover:underline text-left"
                                    data-testid="company-logo-clear">Remove logo</button>
                        ) : null}
                        <div className="text-[10px] text-stone-500">PNG / JPG · under 4 MB</div>
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <div><Label>Address line 1</Label>
                        <Input value={profile.addr_line1 || ""} onChange={set("addr_line1")} disabled={loading} data-testid="cp-addr1" /></div>
                    <div><Label>Address line 2</Label>
                        <Input value={profile.addr_line2 || ""} onChange={set("addr_line2")} disabled={loading} data-testid="cp-addr2" /></div>
                    <div><Label>City</Label>
                        <Input value={profile.city || ""} onChange={set("city")} disabled={loading} data-testid="cp-city" /></div>
                    <div><Label>State</Label>
                        <Input value={profile.state_name || ""} onChange={set("state_name")} disabled={loading} data-testid="cp-state" /></div>
                    <div><Label>PIN</Label>
                        <Input value={profile.pin || ""} onChange={set("pin")} disabled={loading} maxLength={6} data-testid="cp-pin" /></div>
                    <div><Label>Phone</Label>
                        <Input value={profile.phone || ""} onChange={set("phone")} disabled={loading} data-testid="cp-phone" /></div>
                    <div><Label>Email</Label>
                        <Input value={profile.email || ""} onChange={set("email")} disabled={loading} data-testid="cp-email" /></div>
                    <div><Label>PAN</Label>
                        <Input value={profile.pan || ""} onChange={(e) => set("pan")(e.target.value.toUpperCase())} disabled={loading} maxLength={10} className="font-mono" data-testid="cp-pan" /></div>
                </div>

                <div className="pt-3 border-t" style={{ borderColor: "var(--se-border)" }}>
                    <div className="text-sm font-medium mb-2">Bank details (printed on invoice)</div>
                    <div className="grid gap-4 md:grid-cols-2">
                        <div><Label>Bank name</Label>
                            <Input value={profile.bank_name || ""} onChange={set("bank_name")} disabled={loading} data-testid="cp-bank-name" /></div>
                        <div><Label>Branch</Label>
                            <Input value={profile.bank_branch || ""} onChange={set("bank_branch")} disabled={loading} data-testid="cp-bank-branch" /></div>
                        <div><Label>Account number</Label>
                            <Input value={profile.bank_account || ""} onChange={set("bank_account")} disabled={loading} className="font-mono" data-testid="cp-bank-account" /></div>
                        <div><Label>IFSC</Label>
                            <Input value={profile.bank_ifsc || ""} onChange={(e) => set("bank_ifsc")(e.target.value.toUpperCase())} disabled={loading} className="font-mono" data-testid="cp-bank-ifsc" /></div>
                    </div>
                </div>

                <div>
                    <Label>Default terms &amp; conditions</Label>
                    <textarea
                        className="w-full border rounded-sm px-3 py-2 text-sm min-h-[70px]"
                        style={{ borderColor: "var(--se-border)" }}
                        value={profile.default_terms || ""}
                        onChange={set("default_terms")}
                        disabled={loading}
                        placeholder="e.g. Payment due within 30 days. Interest at 18% p.a. on overdue amounts."
                        data-testid="cp-default-terms"
                    />
                </div>

                <div className="flex justify-end pt-2 border-t" style={{ borderColor: "var(--se-border)" }}>
                    <Button onClick={save} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="cp-save">
                        <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : "Save company profile"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


export function InvoiceSeriesCard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", prefix: "", next_number: "1", kind: "sales", is_default: false });
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/invoice-series");
            setRows(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        setCreating(true);
        try {
            await api.post("/invoice-series", {
                name: form.name.trim(),
                prefix: form.prefix,
                next_number: Number(form.next_number) || 1,
                kind: form.kind,
                is_default: form.is_default,
            });
            setForm({ name: "", prefix: "", next_number: "1", kind: "sales", is_default: false });
            toast.success("Series added");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    };

    const startEdit = (r) => {
        setEditingId(r.id);
        setEditForm({ name: r.name, prefix: r.prefix, next_number: String(r.next_number), is_default: r.is_default });
    };
    const saveEdit = async () => {
        try {
            await api.patch(`/invoice-series/${editingId}`, {
                name: editForm.name,
                prefix: editForm.prefix,
                next_number: Number(editForm.next_number) || 1,
                is_default: editForm.is_default,
            });
            setEditingId(null);
            toast.success("Series updated");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (r) => {
        if (!window.confirm(`Delete series "${r.name}"?`)) return;
        try { await api.delete(`/invoice-series/${r.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="invoice-series-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Invoice numbering — series</div>
                <div className="text-xs text-stone-500">
                    Each series has a fixed <b>prefix</b> and an auto-incrementing counter. When you create an invoice from
                    a series, the next number is issued atomically — no two invoices ever share a serial.
                </div>
            </div>

            <div className="p-5 space-y-4">
                {rows.length === 0 ? (
                    <div className="text-xs text-stone-500 italic" data-testid="invoice-series-empty">
                        No series yet. Add your first one below — e.g. <span className="font-mono">JB-B2B-2026-27-</span> starting at 1.
                    </div>
                ) : (
                    <div className="border rounded-md divide-y" style={{ borderColor: "var(--se-border)" }}>
                        {rows.map((r) => {
                            const isEditing = editingId === r.id;
                            const preview = `${(isEditing ? editForm.prefix : r.prefix) || ""}${(isEditing ? editForm.next_number : r.next_number) || 1}`;
                            return (
                                <div key={r.id} className="px-3 py-2.5 text-sm" data-testid={`series-row-${r.id}`}>
                                    {isEditing ? (
                                        <div className="grid gap-2 md:grid-cols-4 items-end">
                                            <div>
                                                <Label className="text-[10px]">Name</Label>
                                                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-8 text-xs" />
                                            </div>
                                            <div>
                                                <Label className="text-[10px]">Prefix</Label>
                                                <Input value={editForm.prefix} onChange={(e) => setEditForm({ ...editForm, prefix: e.target.value })} className="h-8 text-xs font-mono" />
                                            </div>
                                            <div>
                                                <Label className="text-[10px]">Next #</Label>
                                                <Input type="number" value={editForm.next_number} onChange={(e) => setEditForm({ ...editForm, next_number: e.target.value })} className="h-8 text-xs" />
                                            </div>
                                            <div className="flex items-center justify-between gap-2">
                                                <label className="flex items-center gap-1 text-[11px] whitespace-nowrap">
                                                    <input type="checkbox" checked={!!editForm.is_default} onChange={(e) => setEditForm({ ...editForm, is_default: e.target.checked })} />
                                                    Default
                                                </label>
                                                <div className="flex gap-1">
                                                    <Button size="sm" onClick={saveEdit} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid={`series-save-${r.id}`}>Save</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between gap-4 flex-wrap">
                                            <div className="min-w-0">
                                                <div className="font-medium flex items-center gap-2">
                                                    {r.name}
                                                    {r.is_default ? <span className="se-chip se-chip-teal text-[9px]">DEFAULT</span> : null}
                                                    <span className="text-[10px] uppercase tracking-wider text-stone-400">{r.kind}</span>
                                                </div>
                                                <div className="text-[11px] text-stone-500 font-mono">
                                                    Next: <b className="text-purple-700">{preview}</b>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`series-edit-${r.id}`}>Edit</Button>
                                                <Button size="sm" variant="ghost" onClick={() => del(r)} className="text-rose-600" data-testid={`series-delete-${r.id}`}>
                                                    <Trash size={13} />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Add form */}
                <div className="border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                    <div className="text-xs font-medium mb-2">Add a new series</div>
                    <div className="grid gap-3 md:grid-cols-6 items-end">
                        <div className="md:col-span-2">
                            <Label className="text-[10px]">Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                   placeholder="e.g. B2B FY 2026-27" className="h-9 text-sm"
                                   data-testid="series-new-name" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-[10px]">Prefix</Label>
                            <Input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                                   placeholder="JB-B2B-2026-27-" className="h-9 text-sm font-mono"
                                   data-testid="series-new-prefix" />
                        </div>
                        <div>
                            <Label className="text-[10px]">Start #</Label>
                            <Input type="number" value={form.next_number} onChange={(e) => setForm({ ...form, next_number: e.target.value })}
                                   min="1" className="h-9 text-sm" data-testid="series-new-next" />
                        </div>
                        <div>
                            <Label className="text-[10px]">Kind</Label>
                            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                                    className="w-full h-9 text-sm border rounded-sm px-2" style={{ borderColor: "var(--se-border)" }}
                                    data-testid="series-new-kind">
                                <option value="sales">Sales</option>
                                <option value="purchase">Purchase</option>
                            </select>
                        </div>
                        <label className="flex items-center gap-1 text-[11px] md:col-span-2">
                            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                                   data-testid="series-new-default" />
                            Set as default for {form.kind}
                        </label>
                        <div className="md:col-span-4 text-[11px] text-stone-500 font-mono">
                            {form.prefix ? <>Preview: <b className="text-purple-700">{form.prefix}{form.next_number || 1}</b></> : null}
                        </div>
                        <Button onClick={create} disabled={creating || !form.name.trim()}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="series-add-btn">
                            {creating ? "Adding…" : "Add series"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
